/**
 * The "verify this number" strip under the phone field in Settings.
 *
 * Verification carries more weight here than a checkmark. `users.phone` is a
 * field that predates texting — an agent may have typed a client's landline
 * into it years ago — so a number is inert until someone proves they hold it.
 * Completing this is also the consent record, which is why the disclosure sits
 * on the send step rather than in a footnote.
 *
 * Three states, one at a time: nothing saved yet, saved-but-unverified, and
 * verified. The middle one is the only one that does anything.
 */
import { useState } from "react";
import { AlertTriangle, BadgeCheck, Loader2, MessageSquare } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useConfirmPhoneVerification, useStartPhoneVerification } from "@/hooks/useSettings";

// Absolute, because the policies live on the marketing site, not in this app.
const TERMS_URL = "https://www.yourstoop.com/terms-and-conditions";
const PRIVACY_URL = "https://www.yourstoop.com/privacy-policy";

interface PhoneVerificationProps {
  /** E.164 as last saved by the server — not the live form value. */
  savedPhone: string;
  savedPhoneDisplay: string;
  verified: boolean;
  /** True while the profile form has unsaved edits to the number. Verifying a
   *  number the server has not been told about yet would check the wrong one. */
  dirty: boolean;
  /** Re-verifying a number that is verified, but too long ago for phone
   *  sign-in. Texts still reach it, so the warning must not say otherwise. */
  reverify?: boolean;
}

export default function PhoneVerification({ savedPhone, savedPhoneDisplay, verified, dirty, reverify = false }: PhoneVerificationProps) {
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  // Starts unticked on every visit. Carriers reject an opt-in that is implied
  // by pressing some other button, or pre-selected — consent to texts has to
  // be its own deliberate act. "Send code" stays disabled until it is ticked.
  const [agreed, setAgreed] = useState(false);
  const { mutateAsync: start, isPending: sending } = useStartPhoneVerification();
  const { mutateAsync: confirm, isPending: confirming } = useConfirmPhoneVerification();

  // The settled, good state. Deliberately quiet — a green line with a check,
  // no box and no call to action, so the only thing drawing the eye in this
  // section is a number that still needs something doing to it.
  if (verified) {
    return (
      <div
        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-dash-greenDeep/10 px-2.5 py-1.5 text-xs font-medium text-dash-greenDeep"
        data-testid="phone-verified"
      >
        <BadgeCheck className="size-4 shrink-0" />
        {/* Also the "after submit" screenshot in the campaign's opt-in proof,
            so it restates how to leave, not just that the number works. */}
        Verified — Stoop text notifications will reach {savedPhoneDisplay || savedPhone}. Reply STOP
        to any text, or turn them off below, to opt out.
      </div>
    );
  }

  // Order matters: `dirty` is checked first because a number typed into an
  // empty field is both unsaved *and* not-yet-on-the-profile, and "add a mobile
  // number" is plainly wrong to show underneath one the user just typed.
  if (dirty) {
    return (
      <p className="mt-2 text-xs text-dash-muted" data-testid="phone-dirty">
        Save your changes, then verify this number.
      </p>
    );
  }

  if (!savedPhone) {
    return (
      <p className="mt-2 text-xs text-dash-muted" data-testid="phone-unsaved">
        Add a mobile number and save to enable text notifications.
      </p>
    );
  }

  const send = async () => {
    try {
      await start({ sms_consent: true });
      setSent(true);
    } catch {
      /* toasted by the hook */
    }
  };

  const check = async () => {
    try {
      await confirm({ code: code.trim() });
      setSent(false);
      setCode("");
    } catch {
      // Cleared rather than left in place: the next attempt is a fresh one, and
      // a rejected code sitting in the box invites re-submitting it unchanged.
      setCode("");
    }
  };

  return (
    // Warned rather than merely stated: an unverified number is the one state
    // in this section where the feature silently does nothing, and a grey line
    // of body copy reads as information instead of as something to act on.
    <div className="mt-3 rounded-lg border border-dash-amber/40 bg-dash-amber/10 p-3" data-testid="phone-verify">
      {!sent ? (
        <>
          <p className="flex items-start gap-1.5 text-xs text-dash-ink">
            <AlertTriangle className="mt-px size-3.5 shrink-0 text-dash-amber" />
            {reverify ? (
              <span>
                <span className="font-semibold">Verify again</span> to use {savedPhoneDisplay || savedPhone} for sign-in.
              </span>
            ) : (
              <span>
                <span className="font-semibold">Not verified.</span> No texts will be sent to{" "}
                {savedPhoneDisplay || savedPhone} until you confirm it.
              </span>
            )}
          </p>
          {/* The opt-in, quoted verbatim in the A2P 10DLC campaign's message
              flow and screenshotted for its reviewers. Carriers require every
              element here — brand, content, frequency, rates, HELP, STOP, and
              links labelled "Terms of Service" and "Privacy Policy" beside the
              control — so edit the registration, and bump
              `sms_service.SMS_CONSENT_VERSION`, whenever this text changes. */}
          <label className="mt-3 flex cursor-pointer items-start gap-2.5" data-testid="phone-verify-consent">
            <Checkbox
              checked={agreed}
              onCheckedChange={(value) => setAgreed(value === true)}
              className="mt-0.5 shrink-0"
              data-testid="phone-verify-consent-checkbox"
            />
            <span className="text-[11px] leading-relaxed text-dash-muted" data-testid="phone-verify-disclosure">
              I agree to receive text notifications from Stoop about my real estate transactions,
              including verification codes, deadline reminders, overdue tasks and client messages.
              Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP
              to opt out. Consent is not required to use Stoop.{" "}
              <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline hover:text-dash-ink">
                Terms of Service
              </a>{" "}
              ·{" "}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="underline hover:text-dash-ink">
                Privacy Policy
              </a>
            </span>
          </label>
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="dashboardOutline"
              className="h-8 shrink-0 !rounded-lg px-3 text-xs"
              onClick={send}
              disabled={!agreed || sending}
              data-testid="phone-verify-send"
            >
              {sending ? <Loader2 className="mr-1.5 size-3 animate-spin" /> : <MessageSquare className="mr-1.5 size-3" />}
              {sending ? "Sending…" : "Send code"}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            className="h-9 w-36 rounded-lg text-sm"
            data-testid="phone-verify-code"
          />
          <Button
            type="button"
            variant="dashboardPrimary"
            className="h-9 !rounded-lg px-3 text-xs"
            onClick={check}
            disabled={code.length !== 6 || confirming}
            data-testid="phone-verify-confirm"
          >
            {confirming ? "Checking…" : "Verify"}
          </Button>
          <Button
            type="button"
            variant="seamless"
            className="h-9 px-2 text-xs text-dash-muted hover:text-dash-ink"
            onClick={send}
            disabled={sending}
            data-testid="phone-verify-resend"
          >
            {sending ? "Sending…" : "Resend"}
          </Button>
        </div>
      )}
    </div>
  );
}
